import {
  AmplifyDelete,
  AmplifyGet,
  AmplifyPost,
} from '@sunrun/sales-experience-shared/amplify-api-wrapper';
import _uniqueId from 'lodash/uniqueId';
import groupBy from 'lodash/groupBy';
import {
  PipelineDisplayProspect,
  PipelineV2Stage,
} from '@sunrun/experience-ui-components';
import useSWR from 'swr';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  SplatUserType,
  subscribeToAsyncRequest,
} from '@sunrun/sales-experience-shared';
import { logger } from './logger';
import { useHybridToken } from './Auth';
import { Prospect } from 'types/prospect';
import { dateToISO } from 'helpers/formatHelpers';
import {
  LIST_VIEW_MAX_PAGE,
  LIST_VIEW_PAGE_SIZE,
} from 'constants/listViewDataConstants';

type PipelineSalesRep = { id: string; name: string };

type PipelineProspect = {
  key: string; // We get dups where all data is the same this is a unique key for rendering
  street?: string;
  city?: string;
  state?: string;
} & PipelineDisplayProspect;

type PipelineSalesRepsApiResponse = {
  pipelineSalesReps: Array<PipelineSalesRep>;
};

type PipelineManagementApiResponse = {
  prospects: Array<PipelineDisplayProspect>;
  salesReps: Array<PipelineSalesRep>;
};

type ProspectsByStage = {
  [stage in PipelineV2Stage]?: PipelineProspect[] | undefined;
};

type PipelineData = Omit<PipelineManagementApiResponse, 'prospects'> & {
  prospects: ProspectsByStage;
};

const getSalesReps = async (
  hybridToken: string,
  officeOnly?: boolean,
): Promise<PipelineSalesRep[]> => {
  const response = (await AmplifyGet(
    'Get Pipeline Sales Reps',
    'SalesExpDashboardApi',
    `/sales-reps`,
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
      ...(officeOnly
        ? { queryStringParameters: { officeOnly: 'true' } }
        : undefined),
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  )) as PipelineSalesRepsApiResponse;

  return response.pipelineSalesReps.sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

const favoriteStorageKey = 'locallyUpdatedFavorites';

const buildProspectsByStage = (
  pipelineProspects: PipelineDisplayProspect[],
): ProspectsByStage => {
  const storedUpdates = localStorage.getItem(favoriteStorageKey);
  const updatedMap = storedUpdates ? JSON.parse(storedUpdates) : {};
  Object.keys(updatedMap).forEach((key) => {
    const localUpdate = updatedMap[key];
    const minuteAge = Math.floor(
      (new Date().getTime() - localUpdate.cacheTime) / (1000 * 60),
    );
    if (minuteAge > 5) {
      updatedMap[key] = undefined;
    }
  });
  localStorage.setItem(favoriteStorageKey, JSON.stringify(updatedMap));
  const prospectsWithKeys = pipelineProspects.map((prospect) => {
    const prospectWithKey = {
      ...prospect,
      key: _uniqueId(),
    };
    if (updatedMap[prospect.prospectId]) {
      prospectWithKey.starredInPipeline =
        updatedMap[prospect.prospectId].starredInPipeline;
    }
    return prospectWithKey;
  });

  const prospects = groupBy(
    prospectsWithKeys,
    'pipelineStage',
  ) as ProspectsByStage;

  prospects.ARC = ([] as PipelineProspect[])
    .concat(prospects.ARC || [])
    .concat(prospects.Cancelled || [])
    .concat(prospects.Lost || []);

  return prospects;
};

const getCxProPipeline = async (hybridToken: string): Promise<PipelineData> => {
  const response = (await AmplifyGet(
    'Get CX Pro Pipeline',
    'SalesExpDashboardApi',
    `/cx-pro-pipeline`,
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  )) as PipelineManagementApiResponse;

  const prospects = buildProspectsByStage(response.prospects);

  return {
    ...response,
    prospects,
  };
};

const getPipeline = async (
  hybridToken: string,
  salesReps: string[],
): Promise<PipelineData> => {
  const response = (await AmplifyPost(
    'Get Pipeline',
    'SalesExpDashboardApi',
    `/pipeline`,
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
      body: { salesReps },
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  )) as PipelineManagementApiResponse;

  const prospects = buildProspectsByStage(response.prospects);

  return {
    ...response,
    prospects,
  };
};

const useSalesReps = (officeOnly?: boolean, enabled = true) => {
  const { hybridToken } = useHybridToken();

  return useSWR(
    enabled && hybridToken ? ['/sales-reps', hybridToken, officeOnly] : null,
    ([, token, officeOnlyParam]) => {
      if (!token) {
        return null;
      }
      return getSalesReps(token, officeOnlyParam);
    },
    {
      revalidateOnFocus: false,
      errorRetryCount: 3,
    },
  );
};

const getDefaultListViewDateRange = () => {
  const date = new Date();
  const date2 = new Date();
  const startTime = new Date(date.setDate(date.getDate() - 90)).toISOString();
  const endTime = new Date(date2.setDate(date2.getDate() + 1)).toISOString();
  return { startTime, endTime };
};

const getListViewData = async (
  hybridToken: string,
  page: number,
  beginCreatedDate?: string,
  endCreatedDate?: string,
): Promise<Prospect[]> => {
  const { startTime: defaultStart, endTime: defaultEnd } =
    getDefaultListViewDateRange();

  const normalizedStart = beginCreatedDate
    ? dateToISO(beginCreatedDate, false)
    : '';
  const normalizedEnd = endCreatedDate ? dateToISO(endCreatedDate, true) : '';
  const startTime = normalizedStart || defaultStart;
  const endTime = normalizedEnd || defaultEnd;

  const response = await AmplifyGet(
    'Get List View Data',
    'SalesExpDashboardApi',
    `/prospects`,
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
      queryStringParameters: {
        startTime,
        endTime,
        page: page.toString(),
        pageSize: LIST_VIEW_PAGE_SIZE.toString(),
        sortDirection: 'DESC',
        sortField: 'lastmodifieddate',
      },
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  );

  if (response && Array.isArray(response)) {
    return response;
  }
  return [];
};

const useListViewData = (
  beginCreatedDate?: string,
  endCreatedDate?: string,
) => {
  const { hybridToken } = useHybridToken();
  const [accumulatedData, setAccumulatedData] = useState<Prospect[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const [fetchGeneration, setFetchGeneration] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);
  const shouldFetch = useRef(true);

  const dateKey = `${beginCreatedDate ?? ''}-${endCreatedDate ?? ''}`;

  const canFetchPage =
    shouldFetch.current &&
    !isComplete &&
    errorCount < 3 &&
    !!hybridToken &&
    currentPage <= LIST_VIEW_MAX_PAGE;

  const {
    data: listViewData,
    error,
    isLoading,
    isValidating,
  } = useSWR(
    canFetchPage
      ? ['/list-view-data', hybridToken, currentPage, dateKey, fetchGeneration]
      : null,
    ([_, token, page]) =>
      getListViewData(token, page, beginCreatedDate, endCreatedDate),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      errorRetryCount: 0,
    },
  );

  useEffect(() => {
    setAccumulatedData([]);
    setErrorCount(0);
    setIsComplete(false);
    setIsTruncated(false);
    setCurrentPage(1);
    shouldFetch.current = !!hybridToken;
  }, [dateKey, hybridToken]);

  useEffect(() => {
    if (!listViewData) {
      return;
    }

    if (listViewData.length > 0) {
      setAccumulatedData((previousData) => {
        const listViewMap: Record<string, Prospect> = {};
        const newDataRaw = [...previousData, ...listViewData];
        const dataFilteredByProspectId: Prospect[] = [];

        newDataRaw.forEach((prospect) => {
          const { prospectId } = prospect;
          if (!(prospectId in listViewMap)) {
            listViewMap[prospectId] = prospect;
            dataFilteredByProspectId.push(prospect);
          }
        });

        return dataFilteredByProspectId;
      });

      const nextPage = currentPageRef.current + 1;
      if (nextPage > LIST_VIEW_MAX_PAGE) {
        setIsTruncated(true);
        setIsComplete(true);
        shouldFetch.current = false;
        return;
      }
      setCurrentPage(nextPage);
      return;
    }

    setIsComplete(true);
    shouldFetch.current = false;
  }, [listViewData]);

  useEffect(() => {
    if (error) {
      setErrorCount((prev) => {
        const newErrorCount = prev + 1;

        if (newErrorCount >= 3) {
          setIsComplete(true);
          shouldFetch.current = false;
        } else {
          setCurrentPage((current) => current + 1);
        }

        return newErrorCount;
      });
    }
  }, [error]);

  const refresh = useCallback(() => {
    setAccumulatedData([]);
    setErrorCount(0);
    setIsComplete(false);
    setIsTruncated(false);
    setCurrentPage(1);
    shouldFetch.current = !!hybridToken;
    setFetchGeneration((generation) => generation + 1);
  }, [hybridToken]);

  return {
    data: accumulatedData,
    error: errorCount >= 3 ? error : null,
    isLoading: isLoading && !isComplete,
    isValidating,
    isTruncated,
    refresh,
  };
};

const usePipeline = (splatUserType?: SplatUserType, salesReps?: string[]) => {
  const { hybridToken } = useHybridToken();

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    ['/pipeline', splatUserType, hybridToken, salesReps],
    ([, splatUserType, token, salesReps]) => {
      if (!splatUserType) {
        return null;
      }
      if (!token) {
        return null;
      }

      if (splatUserType === 'CX Pro') {
        return getCxProPipeline(token);
      }

      if (!salesReps || salesReps.length === 0) {
        return null;
      }
      return getPipeline(token, salesReps);
    },
    {
      revalidateOnFocus: false,
      errorRetryCount: 3,
    },
  );

  const refresh = useCallback(() => mutate(), [mutate]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    refresh,
  };
};

const updateFavorite = async (
  hybridToken: string,
  pipelineProspect: PipelineProspect,
  starredInPipeline: boolean,
) => {
  const response = await AmplifyPost<{ asyncRequestId: string }>(
    'Update Favorite',
    'SalesExpDashboardApi',
    `/favorite/${pipelineProspect.prospectId}`,
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
      body: {
        prospectId: pipelineProspect.prospectId,
        starredInPipeline,
      },
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  );

  await subscribeToAsyncRequest(hybridToken, response.asyncRequestId).then(
    () => {
      const storedUpdates = localStorage.getItem(favoriteStorageKey);
      const updatedMap = storedUpdates ? JSON.parse(storedUpdates) : {};
      updatedMap[pipelineProspect.prospectId] = {
        starredInPipeline,
        cacheTime: new Date().getTime(),
      };
      localStorage.setItem(favoriteStorageKey, JSON.stringify(updatedMap));
    },
  );
};

const deletePipelineCache = async (hybridToken: string, salesRepId: string) => {
  await AmplifyDelete(
    'Delete Pipeline Cache',
    'SalesExpDashboardApi',
    '/pipeline-cache',
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
      queryStringParameters: {
        salesReps: salesRepId,
      },
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  );
};

export type {
  PipelineProspect,
  PipelineSalesRep,
  ProspectsByStage,
  PipelineData,
};
export {
  deletePipelineCache,
  usePipeline,
  useSalesReps,
  updateFavorite,
  useListViewData,
};
